import { useState } from 'react';
import { api } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { UserX, UserCheck, KeyRound, Search, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

export function AdminUsersTab({ users, onToggleSuspend }) {
  const [resetTarget, setResetTarget] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all'); // 'all' | 'client' | 'provider' | 'admin' | 'guest'

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setResetting(true);
    try {
      const res = await api.post(`/admin/users/${resetTarget.user_id}/reset-password`, { password: newPassword });
      toast.success(res.data.message);
      setResetTarget(null);
      setNewPassword('');
      setShowPassword(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  // Per-role counts, used for the filter pill badges so admin can see the
  // population split at a glance without re-counting in their head.
  const roleCounts = users.reduce((acc, u) => {
    const r = u.role || 'client';
    acc[r] = (acc[r] || 0) + 1;
    return acc;
  }, {});

  const roleMatches = (u) => {
    if (roleFilter === 'all') return true;
    return (u.role || 'client') === roleFilter;
  };

  const filtered = users.filter((u) => {
    if (!roleMatches(u)) return false;
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    return u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
  });

  const ROLE_FILTERS = [
    { key: 'all', label: 'All' },
    { key: 'client', label: 'Clients' },
    { key: 'provider', label: 'Providers' },
    { key: 'admin', label: 'Admins' },
    { key: 'guest', label: 'Guests' },
  ];

  return (
    <div>
      {/* Search + role filters */}
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative w-full sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search users by name or email..."
            className="input-zuriya pl-9"
            data-testid="users-search-input"
          />
        </div>
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="users-role-filter">
          {ROLE_FILTERS.map(({ key, label }) => {
            const isActive = roleFilter === key;
            const count = key === 'all' ? users.length : (roleCounts[key] || 0);
            return (
              <button
                key={key}
                type="button"
                onClick={() => setRoleFilter(key)}
                className={`text-xs font-medium rounded-full px-3 py-1.5 border transition-colors ${
                  isActive
                    ? 'bg-[#1A1A1A] text-white border-[#1A1A1A]'
                    : 'bg-white text-stone-600 border-[#E6E4DD] hover:border-[#D4AF37] hover:text-[#1A1A1A]'
                }`}
                data-testid={`role-filter-${key}`}
              >
                {label} <span className={`ml-1 ${isActive ? 'text-white/70' : 'text-stone-400'}`}>{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="card-surface overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#F9F6F0]">
              <tr>
                <th className="text-left py-3 px-4 text-stone-600 font-medium text-sm">Name</th>
                <th className="text-left py-3 px-4 text-stone-600 font-medium text-sm">Email</th>
                <th className="text-left py-3 px-4 text-stone-600 font-medium text-sm">Role</th>
                <th className="text-left py-3 px-4 text-stone-600 font-medium text-sm">Joined</th>
                <th className="text-left py-3 px-4 text-stone-600 font-medium text-sm">Status</th>
                <th className="text-left py-3 px-4 text-stone-600 font-medium text-sm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.user_id} className={`border-b border-[#E6E4DD] hover:bg-[#F9F6F0] ${u.suspended ? 'opacity-50' : ''}`}>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-[#D4AF37] flex items-center justify-center text-white text-xs">{u.name?.charAt(0)}</div>
                      <span className="font-medium text-[#1A1A1A] text-sm">{u.name}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-stone-600">{u.email}</td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      u.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                      u.role === 'provider' ? 'bg-blue-100 text-blue-700' :
                      u.role === 'guest' ? 'bg-stone-100 text-stone-600' :
                      'bg-green-100 text-green-700'
                    }`}>{u.role}</span>
                  </td>
                  <td className="py-3 px-4 text-sm text-stone-500">{u.created_at ? new Date(u.created_at).toLocaleDateString('en-GB') : ','}</td>
                  <td className="py-3 px-4">
                    {u.suspended
                      ? <span className="text-xs text-red-500 font-medium" data-testid={`user-status-${u.user_id}`}>Suspended</span>
                      : <span className="text-xs text-green-600 font-medium" data-testid={`user-status-${u.user_id}`}>Active</span>}
                  </td>
                  <td className="py-3 px-4">
                    {u.role !== 'admin' && (
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => { setResetTarget(u); setNewPassword(''); setShowPassword(false); }}
                          className="h-8 px-2 text-[#D4AF37] hover:text-[#b8962e] hover:bg-amber-50"
                          data-testid={`reset-pw-${u.user_id}`}
                        >
                          <KeyRound className="h-3.5 w-3.5 mr-1" />Reset Password
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onToggleSuspend(u)}
                          className={`h-8 px-2 ${u.suspended ? 'text-green-600' : 'text-red-400'}`}
                          data-testid={`suspend-toggle-${u.user_id}`}
                        >
                          {u.suspended ? <><UserCheck className="h-3.5 w-3.5 mr-1" />Lift suspension</> : <><UserX className="h-3.5 w-3.5 mr-1" />Suspend</>}
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-stone-400 text-sm">
                    {searchQuery || roleFilter !== 'all' ? 'No users match your filters.' : 'No users found.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetTarget} onOpenChange={(open) => { if (!open) setResetTarget(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-[#D4AF37]" />
              Reset Password
            </DialogTitle>
          </DialogHeader>
          {resetTarget && (
            <div>
              <div className="p-3 bg-[#F9F6F0] rounded-lg mb-4">
                <p className="text-sm font-medium text-[#1A1A1A]">{resetTarget.name}</p>
                <p className="text-xs text-stone-500">{resetTarget.email}</p>
                <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                  resetTarget.role === 'provider' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                }`}>{resetTarget.role}</span>
              </div>
              <Label className="text-sm font-medium text-[#1A1A1A]">New Password</Label>
              <div className="relative mt-1.5">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="8+ characters with a capital, number and symbol"
                  className="input-zuriya pr-10"
                  data-testid="reset-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-stone-400 mt-2">They will be signed out on every device and need to log in with this new password. Share it with them securely.</p>
            </div>
          )}
          <DialogFooter className="mt-4 gap-2">
            <Button variant="outline" onClick={() => setResetTarget(null)}>Cancel</Button>
            <Button
              onClick={handleResetPassword}
              disabled={!newPassword || newPassword.length < 8 || resetting}
              className="btn-primary gap-2"
              data-testid="confirm-reset-password-btn"
            >
              {resetting ? 'Resetting...' : 'Reset Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
